import { financeConfig } from 'configurations';
import {
    BasketDetailsModel,
    ProductTypes,
    BasketAddressModel
} from 'models/Basket';
import {
    PaymentDetailsModel,
    IfcPaymentStateModel,
} from 'models/Payment';
import { MAX_DECIMAL_PLACES } from 'helpers';
import { IfcCalculatorConfigModel } from 'models';

const MONTHLY_PAYMENT_ROUND = 2;

export class IfcPaymentHelper {
    public getMinDepositPercent(paymentData: IfcPaymentStateModel): number {
        return paymentData.ifcConfig.minDepositPercent;
    }

    public isIfcPaymentAvailable(basket: BasketDetailsModel, isIfcFormSubmitted: boolean, paymentData: IfcPaymentStateModel): boolean {
        return this.isIfcPaymentAvailableByPrice(basket.totalPriceWithTax.value, paymentData)
            && this.isIfcPaymentAvailableByDelivery(basket.deliveryAddress)
            && !isIfcFormSubmitted
            && !basket.hasDwellProducts();
    }

    public isIfcPaymentAvailableByPrice(totalPriceWithTax: number, paymentData: IfcPaymentStateModel): boolean {
        return totalPriceWithTax >= this.getMinBasketTotalForIfcPayment(paymentData);
    }

    public isIfcPaymentAvailableByDelivery(deliveryAddress: BasketAddressModel | null): boolean {
        return !deliveryAddress || (deliveryAddress && !deliveryAddress.postcode.isEirCode);
    }

    public getMinBasketTotalForIfcPayment(paymentData: IfcPaymentStateModel): number {
        let calculatedEligibilityOfFinance = this.getCalculatedEligibilityOfFinance(paymentData);
        let actualEligibilityOfFinance = calculatedEligibilityOfFinance > paymentData.ifcConfig.eligibilityOfFinance
            ? calculatedEligibilityOfFinance
            : paymentData.ifcConfig.eligibilityOfFinance;

        return actualEligibilityOfFinance;
    }

    public initializeIfc(paymentDetails: PaymentDetailsModel, basket: BasketDetailsModel, config: IfcCalculatorConfigModel): IfcPaymentStateModel {
        let paymentData = new IfcPaymentStateModel();
        paymentData.ifcConfig = config;
        this.fillIfcConfigWithHardcodedValues(paymentData.ifcConfig);

        let totalPriceWithTax = basket.totalPriceWithTax.value;

        if (!this.isIfcPaymentAvailableByPrice(totalPriceWithTax, paymentData)) {
            return paymentData;
        }

        this.setLimits(paymentData, basket);

        if (!paymentDetails.isEmptyModel && !this.shouldRecalculateSavedPaymentDetails(paymentDetails, paymentData, basket)) {
            paymentData.deposit = paymentDetails.ifcDeposit;
            paymentData.depositPercentage = paymentDetails.ifcDepositPercentage;
            paymentData.months = paymentDetails.ifcNumMonths;
            paymentData.monthlyPayment = paymentDetails.ifcMonthlyAmount;

            return paymentData;
        }

        let initDepositPercentage = !paymentDetails.isEmptyModel
            ? paymentDetails.ifcDepositPercentage
            : paymentData.ifcConfig.initDepositPercent;

        let initDeposit = !paymentDetails.isEmptyModel && paymentDetails.total === totalPriceWithTax
            ? paymentDetails.ifcDeposit
            : Math.ceil(totalPriceWithTax * initDepositPercentage / 100);

        if (initDeposit > paymentData.maxDeposit || initDeposit < paymentData.minDeposit) {
            if (initDeposit > paymentData.maxDeposit) {
                initDeposit = paymentData.maxDeposit;
            }

            if (initDeposit < paymentData.minDeposit) {
                initDeposit = paymentData.minDeposit;
            }
        }

        let months = paymentDetails.ifcNumMonths;
        if (months < paymentData.minMonths) {
            months = paymentData.minMonths;
        }

        if (months > paymentData.maxMonths) {
            months = paymentData.maxMonths;
        }

        paymentData.deposit = initDeposit;
        paymentData.depositPercentage = this.getDepositPercentage(initDeposit, totalPriceWithTax);
        paymentData.months = months;
        paymentData.monthlyPayment = this.getMonthlyPayment(totalPriceWithTax, initDeposit, months);

        return paymentData;
    }

    public updateDeposit(totalPriceWithTax: number, prevPaymentData: IfcPaymentStateModel, deposit: number): IfcPaymentStateModel {
        let paymentData = { ...prevPaymentData };

        if (deposit > paymentData.maxDeposit) {
            deposit = paymentData.maxDeposit;
        }

        if (deposit < paymentData.minDeposit) {
            deposit = paymentData.minDeposit;
        }

        paymentData.deposit = deposit;
        paymentData.depositPercentage = this.getDepositPercentage(deposit, totalPriceWithTax);
        paymentData.monthlyPayment = this.getMonthlyPayment(totalPriceWithTax, deposit, paymentData.months);

        return paymentData;
    }

    public incrementDeposit(totalPriceWithTax: number, prevPaymentData: IfcPaymentStateModel): IfcPaymentStateModel {
        let paymentData = { ...prevPaymentData };

        // trying to find the closest deposit value that:
        // 1. more than current value
        // 2. multiple of depositSliderIncrement (50) (50, 100, 150,...)
        let multipleDepositStickingUp = ((Math.trunc(paymentData.deposit / financeConfig.ifc.depositSliderIncrement) + 1) * financeConfig.ifc.depositSliderIncrement);

        let newDeposit = multipleDepositStickingUp;
        if (newDeposit >= paymentData.maxDeposit) {
            newDeposit = paymentData.maxDeposit;
        }

        return this.updateDeposit(totalPriceWithTax, paymentData, newDeposit);
    }

    public decrementDeposit(totalPriceWithTax: number, prevPaymentData: IfcPaymentStateModel): IfcPaymentStateModel {
        let paymentData = { ...prevPaymentData };

        // trying to find the closest deposit value that:
        // 1. less than current value
        // 2. multiple of depositSliderIncrement (50) (50, 100, 150,...)
        let multipleDepositStickingDown = ((Math.trunc(paymentData.deposit / financeConfig.ifc.depositSliderIncrement)) * financeConfig.ifc.depositSliderIncrement);
        if (multipleDepositStickingDown === paymentData.deposit) {
            multipleDepositStickingDown -= financeConfig.ifc.depositSliderIncrement;
        }

        let newDeposit = multipleDepositStickingDown;

        if (newDeposit <= paymentData.minDeposit) {
            newDeposit = paymentData.minDeposit;
        }

        return this.updateDeposit(totalPriceWithTax, paymentData, newDeposit);
    }

    public updateMonths(totalPriceWithTax: number, prevPaymentData: IfcPaymentStateModel, months: number): IfcPaymentStateModel {
        let paymentData = { ...prevPaymentData };

        if (months > paymentData.maxMonths) {
            months = paymentData.maxMonths;
        }

        if (months < paymentData.minMonths) {
            months = paymentData.minMonths;
        }

        paymentData.months = months;
        paymentData.monthlyPayment = this.getMonthlyPayment(totalPriceWithTax, paymentData.deposit, months);

        return paymentData;
    }

    public incrementMonths(totalPriceWithTax: number, prevPaymentData: IfcPaymentStateModel): IfcPaymentStateModel {
        let paymentData = { ...prevPaymentData };

        let newMonths = paymentData.months + financeConfig.ifc.monthlySliderIncrement;

        if (newMonths > paymentData.maxMonths) {
            newMonths = paymentData.maxMonths;
        }

        return this.updateMonths(totalPriceWithTax, paymentData, newMonths);
    }

    public decrementMonths(totalPriceWithTax: number, prevPaymentData: IfcPaymentStateModel): IfcPaymentStateModel {
        let paymentData = { ...prevPaymentData };

        let newMonths = paymentData.months - financeConfig.ifc.monthlySliderIncrement;

        if (newMonths < paymentData.minMonths) {
            newMonths = paymentData.minMonths;
        }

        return this.updateMonths(totalPriceWithTax, paymentData, newMonths);
    }

    private setLimits(paymentData: IfcPaymentStateModel, basket: BasketDetailsModel): void {
        let donateSum = 0;
        if (basket.hasDonationProduct()) {
            donateSum = basket.entries.find(i => i.product.type === ProductTypes.Donation)!.totalPrice.value;
        }

        let totalPrice = basket.totalPriceWithTax.value;
        let totalPriceWithoutDonate = totalPrice - donateSum;

        let minDeposit = paymentData.ifcConfig.minDepositPercent * totalPriceWithoutDonate / 100;
        if (minDeposit < paymentData.ifcConfig.minDepositValue) {
            minDeposit = paymentData.ifcConfig.minDepositValue;
        }

        minDeposit += donateSum;

        if (totalPrice - minDeposit > paymentData.ifcConfig.maxBalanceAmount) {
            minDeposit = totalPrice - paymentData.ifcConfig.maxBalanceAmount;
        }

        minDeposit = Math.ceil(minDeposit);

        let maxAllowedDeposit = totalPrice - paymentData.ifcConfig.minBalanceAmount;
        let maxDeposit = paymentData.ifcConfig.maxDepositValue;

        if (maxDeposit > maxAllowedDeposit || maxDeposit < minDeposit) {
            maxDeposit = maxAllowedDeposit;
        }

        maxDeposit = Math.trunc(maxDeposit);

        paymentData.minDeposit = minDeposit;
        paymentData.maxDeposit = maxDeposit;

        paymentData.minMonths = paymentData.ifcConfig.minFinanceMonths;
        paymentData.maxMonths = paymentData.ifcConfig.maxFinanceMonths;
    }

    private isCorrectDeposit(deposit: number, paymentData: IfcPaymentStateModel): boolean {
        return deposit >= paymentData.minDeposit && deposit <= paymentData.maxDeposit;
    }

    private isCorrectMonths(months: number, paymentData: IfcPaymentStateModel): boolean {
        return months >= paymentData.ifcConfig.minFinanceMonths && months <= paymentData.ifcConfig.maxFinanceMonths;
    }

    private getDepositPercentage(deposit: number, basketTotal: number): number {
        return Number(((deposit / basketTotal) * 100).toFixed(MAX_DECIMAL_PLACES));
    }

    private shouldRecalculateSavedPaymentDetails(savedPaymentDetails: PaymentDetailsModel, paymentData: IfcPaymentStateModel, basket: BasketDetailsModel): boolean {
        return savedPaymentDetails.total !== basket.totalPriceWithTax.value
            || !this.isCorrectDeposit(savedPaymentDetails.ifcDeposit, paymentData)
            || !this.isCorrectMonths(savedPaymentDetails.ifcNumMonths, paymentData);
    }

    private getMonthlyPayment(basketTotal: number, deposit: number, months: number): number {
        return Number(((basketTotal - deposit) / months).toFixed(MONTHLY_PAYMENT_ROUND));
    }

    private getCalculatedEligibilityOfFinance(paymentData: IfcPaymentStateModel): number {
        let calulatedOnMinDepositValueEligibility = paymentData.ifcConfig.minBalanceAmount + paymentData.ifcConfig.minDepositValue;

        // remaining percent from orderTotal should be at least equal to minBalanceAmount
        // e.g. minDepositPercent = 60%, 40% from total should be at least equal 500, from these values we can get eligibility 500/0.4 = 1250
        let remainingOrderPercent = 100 - paymentData.ifcConfig.minDepositPercent;
        let calulatedOnMinDepositPercentEligibility = Math.ceil(paymentData.ifcConfig.minBalanceAmount / (remainingOrderPercent / 100));

        return calulatedOnMinDepositPercentEligibility > calulatedOnMinDepositValueEligibility
            ? calulatedOnMinDepositPercentEligibility
            : calulatedOnMinDepositValueEligibility;
    }

    private fillIfcConfigWithHardcodedValues(apiConfig: IfcCalculatorConfigModel) {
        apiConfig.minBalanceAmount = financeConfig.ifc.fixedValues.minBalanceAmount;
        apiConfig.maxBalanceAmount = financeConfig.ifc.fixedValues.maxBalanceAmount;
    }
}

export const ifcPaymentHelper = new IfcPaymentHelper();